import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { APIResponse, BasicResponse } from '../_types/api-responses.types';
import {
  Commission,
  CommissionDetailsUpdateRequest,
  GetCommissionsResponse,
  GetExtendedCommissionsRequest,
  GetExtendedCommissionsResponse,
} from '../_types/commission.types';
import {
  AgentCommissionDetails,
  GetAgentCommissionsPageRequest,
} from '../_types/fee.types';
import { SubCategory } from '../_types/product.types';
import { Globals } from '../globals';

@Injectable({
  providedIn: 'root',
})
export class CommissionService {
  private _getRoleUserList(obj: { agentId: any }) {
    throw new Error('Method not implemented.');
  }

  _headers: HttpHeaders;

  _baseURL: any;

  user: any;

  deviceId: number;

  constructor(
    private httpClient: HttpClient,
    private globaols: Globals
  ) {
    this.deviceId = Date.now();
    this.user = this.globaols.getUser();
    this._headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('partner-id', this.user.partnerId);
    this._baseURL = environment.apiUrl;
  }

  get headers() {
    return this._headers.set('Access-Token', this.globaols.getUser().token);
  }

  _addCommission(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/add/agent-commission`,
      obj,
      { headers: this.headers }
    );
  }

  _updateCommission(obj: any): Observable<any> {
    return this.httpClient.put(
      `${this._baseURL}api/fee/v1/update/agent-commission`,
      obj,
      { headers: this.headers }
    );
  }

  _getCommissionDetailsById(id: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}api/fee/v1/get/fee`, {
      headers: this.headers,
    });
  }

  _getCommissionDetailsPageByAgent(
    obj: GetAgentCommissionsPageRequest
  ): Observable<APIResponse<{ feeDetailsList: AgentCommissionDetails[] }>> {
    return this.httpClient.post<
      APIResponse<{ feeDetailsList: AgentCommissionDetails[] }>
    >(`${this._baseURL}api/fee/v1/get/agent/agent-commission/page`, obj, {
      headers: this.headers,
    });
  }

  _deleteCommision(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/delete/agent-commission`,
      obj,
      { headers: this.headers }
    );
  }

  _getCommissionProfile(): Observable<any> {
    const obj = {
      partnerId: this.user.partnerId,
    };

    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/profiles`,
      obj,
      { headers: this.headers }
    );
  }

  _addProductCommission(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/add/product-commission`,
      obj,
      { headers: this.headers }
    );
  }

  _getFeeCategory(insuranceType?: string | null): Observable<any> {
    const obj = {
      partnerId: '',
      insuranceType,
    };
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/category`,
      obj,
      { headers: this.headers }
    );
  }

  _getFeeSubCategory(
    obj: any,
    activeOnly: boolean = false
  ): Observable<APIResponse<{ subCategoryList: SubCategory[] }>> {
    return this.httpClient.post<
      APIResponse<{ subCategoryList: SubCategory[] }>
    >(
      `${this._baseURL}api/fee/v1/get/sub-category`,
      { ...obj, activeOnly },
      {
        headers: this.headers,
      }
    );
  }

  _listAllCategory(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/product-commission`,
      obj,
      { headers: this.headers }
    );
  }

  _getAllSubcategory(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/category/product-commission`,
      obj,
      { headers: this.headers }
    );
  }

  _listAllProduct(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/sub-category/product-commission`,
      obj,
      { headers: this.headers }
    );
  }

  _updateProduct(obj: any): Observable<any> {
    return this.httpClient.put(
      `${this._baseURL}api/fee/v1/update/product-commission`,
      obj,
      { headers: this.headers }
    );
  }

  _deleteCategory(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/delete/category`,
      obj,
      { headers: this.headers }
    );
  }

  _deleteSubCategory(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/delete/sub-category`,
      obj,
      { headers: this.headers }
    );
  }

  _deleteProduct(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/delete/product`,
      obj,
      { headers: this.headers }
    );
  }

  _getCategorytAgentList(obj: any): Observable<any> {
    if (this.user.userType === 'PARTNER') {
      obj.partnerId = this.user.partnerId;
    }
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/category/agents`,
      obj,
      { headers: this.headers }
    );
  }

  _getAllProposalProduct(
    obj: any,
    activeOnly: boolean = false
  ): Observable<any> {
    if (this.user.userType === 'PARTNER') {
      obj.partnerId = this.user.partnerId;
    }
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/product`,
      { ...obj, activeOnly },
      {
        headers: this.headers,
      }
    );
  }

  _getCommission(obj: any): Observable<any> {
    if (this.user.userType === 'PARTNER') {
      obj.partnerId = this.user.partnerId;
    }
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/commission`,
      obj,
      { headers: this.headers }
    );
  }

  _deleteProductCommission(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/delete/product-commission`,
      obj,
      { headers: this.headers }
    );
  }

  _getAgentCategory(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/agent/category`,
      obj,
      { headers: this.headers }
    );
  }

  _getAgentSubCategory(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/fee/v1/get/agent/sub-category`,
      obj,
      { headers: this.headers }
    );
  }

  getCommissions(
    request: GetExtendedCommissionsRequest,
    isPartner = false
  ): Observable<APIResponse<GetExtendedCommissionsResponse>> {
    return this.httpClient.post<APIResponse<GetExtendedCommissionsResponse>>(
      `${this._baseURL}api/insurance/v1/get/commissions${isPartner ? '/partner' : ''}`,
      request,
      {
        headers: this.headers,
      }
    );
  }

  getSingleCommission(id: string): Observable<APIResponse<Commission>> {
    return this.httpClient.post<APIResponse<Commission>>(
      `${this._baseURL}api/insurance/v1/get/single-commission`,
      { commissionId: id },
      {
        headers: this.headers,
      }
    );
  }

  updateCommissionDetails(
    obj: CommissionDetailsUpdateRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}api/insurance/v1/commission/update`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  getProposalCommissions(
    proposalId: string
  ): Observable<APIResponse<GetCommissionsResponse>> {
    return this.httpClient.post<APIResponse<GetCommissionsResponse>>(
      `${this._baseURL}api/insurance/v1/get/commissions/proposal`,
      { proposalId },
      {
        headers: this.headers,
      }
    );
  }
}
